import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SortIcon from './SortIcon'
import {
  compareNumber, compareText, nextSort, text, thClass,
  type SortDir,
} from './topTableSort'

/**
 * Top modeller-grid shared by Klasse-detalj, Selger-detalj, Kjøper-detalj and
 * Org-detalj (#586 AC2).
 *
 * One row per (merke, modell) pair, where the Top merker grid has one row per
 * brand. The two answer different questions: "which brands move here" versus
 * "which individual models move here" — a Yaris at 6 and a Corolla at 4 used
 * to collapse into the single word "Yaris" on the brand grid.
 *
 * Andel % is of the rows this grid shows, so the column sums to 100. Transfers
 * with no model recorded are excluded server-side rather than bundled into an
 * "ukjent" row, so that total is smaller than the segment's.
 */

export interface TopModellRow {
  merke: string
  modell: string
  count: number
  share_pct: number
}

type SortCol = 'merke' | 'modell' | 'count' | 'share_pct'
type Sort = { col: SortCol; dir: SortDir } | null

const _nb = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 1 })

export function compare(a: TopModellRow, b: TopModellRow, col: SortCol, dir: SortDir): number {
  if (col === 'merke') return compareText(a.merke, b.merke, dir)
  if (col === 'modell') return compareText(a.modell, b.modell, dir)
  const va = col === 'count' ? a.count : a.share_pct
  const vb = col === 'count' ? b.count : b.share_pct
  return compareNumber(va, vb, dir)
}

interface Props {
  rows: TopModellRow[]
}

export default function TopModellerTable({ rows }: Props) {
  const { t } = useTranslation('bruktbil')
  const [sort, setSort] = useState<Sort>(null)

  const sorted = sort ? [...rows].sort((a, b) => compare(a, b, sort.col, sort.dir)) : rows

  const columns: { col: SortCol; label: string; right?: boolean }[] = [
    { col: 'merke', label: t('common.merke') },
    { col: 'modell', label: t('common.modell') },
    { col: 'count', label: t('common.antall'), right: true },
    { col: 'share_pct', label: t('common.andelPct'), right: true },
  ]

  if (rows.length === 0) {
    return <div className="card p-6 text-sm text-gray-400 dark:text-gray-500">{t('common.ingenData')}</div>
  }

  return (
    <div className="card overflow-x-auto" data-testid="top-modeller-table">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
          <tr>
            {columns.map(c => (
              <th
                key={c.col}
                className={`${thClass}${c.right ? ' text-right' : ''}`}
                onClick={() => setSort(s => nextSort(s, c.col))}
              >
                {c.label} <SortIcon active={sort?.col === c.col} dir={sort?.dir ?? 'asc'} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr
              key={`${r.merke}|${r.modell}`}
              className={i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}
            >
              <td className="px-3 py-1.5">{text(r.merke)}</td>
              <td className="px-3 py-1.5">{text(r.modell) || '—'}</td>
              <td className="px-3 py-1.5 tabular-nums text-right">{_nb.format(r.count)}</td>
              <td className="px-3 py-1.5 tabular-nums text-right">{_nb.format(r.share_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
